import { Formik, FormikValues } from "formik";
import { createContext, useEffect, useState } from "react";

import { API_CALLS } from "../../../../../../API/api_calls";
import { useAppSelector } from "../../../../../../redux";
import { MeProps } from "../../../../../../redux/slices/me/Me";
import { CreatePostModalProps } from "./CreatePostModal";
import ModalContent from "./ModalContent";
import ModalHeader from "./ModalHeader";
import {
  CHOICE_COUNT_INITIAL_VALUE,
  createPostValidation,
  initialValues,
} from "./utils";
import { SchedulePostModal } from "../../../../../Common";
import { scheduledPostUtils } from "../../../../../Common/SchedulePostModal/utils";

export const CreatePostModalContext = createContext(
  {} as CreatePostModalProps.Context
);

const CreatePostModal = (props: CreatePostModalProps.IProps) => {
  const { post_id } = useAppSelector((state) => state.posts);
  const { id } = useAppSelector((state) => state.postGIF.postGIF);
  const { emojis, first_name, last_name, anonymous_persona } = useAppSelector(
    (state) => state.me
  );
  const emojiIds = Object.keys(emojis);
  const [isTopicOpen, setIsTopicOpen] = useState(false);
  const [topics, setTopics] = useState<string[]>([]);
  const [selectedPostAsOption, setSelectedPostAsOption] = useState(
    first_name + " " + last_name
  );
  const [choiceCount, setChoiceCount] = useState(CHOICE_COUNT_INITIAL_VALUE);
  const [selectedCollaborator, setSelectedCollaborator] = useState<
    MeProps.BasicUserInfo[]
  >([]);
  const [urlData, setUrlData] = useState<CreatePostModalProps.CustomURL | null>(
    null
  );

  const {
    pollOpen,
    sliderOpen,
    schedulePostOpen,
    isAudioOpen,
    isVideoOpen,
    resetPostState,
    setSchedulePostOpen,
  } = props;

  useEffect(() => {
    API_CALLS.POSTS.createPostDraft(isVideoOpen, isAudioOpen);
    // eslint-disable-next-line
  }, []);

  const postSubmit = (values: FormikValues) => {
    const newValues = { ...values };
    delete newValues.duration;
    delete newValues.content;
    delete newValues.question;
    delete newValues.slider_question;
    delete newValues.right_emoji_id;
    delete newValues.left_emoji_id;
    delete newValues.delivery_date;
    delete newValues.audio_title;
    delete newValues.audio_description;
    delete newValues.video_title;
    delete newValues.video_description;
    const newChoice: FormikValues = {};

    Object.values(newValues).forEach((choice, index) => {
      newChoice[`choice_${index + 1}`] = choice;
    });

    API_CALLS.POSTS.activateAPost(
      {
        content: values.content || null,
        delivery_date: values.delivery_date
          ? values.delivery_date.toISOString()
          : null,
        node_tree: "",
        post_id,
        topics: topics,
        url_id: urlData?.id ?? null,
        gif: id ? { id } : null,
        poll: pollOpen
          ? {
              ...newChoice,
              post_id,
              question: values.question,
              duration: values.duration,
            }
          : null,
        slider: sliderOpen
          ? {
              left_emoji_id: values.left_emoji_id,
              post_id,
              question: values.slider_question,
              right_emoji_id: values.right_emoji_id,
            }
          : null,
        audio_media: isAudioOpen
          ? {
              description: values.audio_description,
              title: values.audio_title,
              post_id,
            }
          : null,
        video_media: isVideoOpen
          ? {
              description: values.video_description,
              title: values.video_title,
              post_id,
            }
          : null,
        collaborators: selectedCollaborator.map((user) => user.id),
        is_anonymous: selectedPostAsOption === anonymous_persona,
      },
      resetPostState
    );
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        left_emoji_id: emojiIds[0],
        right_emoji_id: emojiIds[1],
      }}
      validationSchema={createPostValidation({
        choiceCount,
        pollOpen,
        sliderOpen,
      })}
      onSubmit={postSubmit}
      validateOnChange={false}
      isInitialValid={false}
      validateOnBlur
    >
      {(formik) => {
        const contextValue: CreatePostModalProps.Context = {
          ...props,
          urlData,
          setUrlData,
          choiceCount,
          setChoiceCount,
          formik,
          resetPostState,
          isTopicOpen,
          setIsTopicOpen,
          topics,
          setTopics,
          selectedCollaborator,
          setSelectedCollaborator,
          selectedPostAsOption,
          setSelectedPostAsOption,
        };

        return (
          <CreatePostModalContext.Provider value={contextValue}>
            {schedulePostOpen ? (
              <SchedulePostModal
              title="Schedule post"
                onSubmit={(values) => {
                  formik.setFieldValue(
                    "delivery_date",
                    scheduledPostUtils.createDate(values)
                  );
                  setSchedulePostOpen(false);
                }}
                setSchedulePostOpen={setSchedulePostOpen}
                deliveryDate={formik?.values?.delivery_date ?? ""}
              />
            ) : (
              <>
                <ModalHeader />
                <ModalContent />
              </>
            )}
          </CreatePostModalContext.Provider>
        );
      }}
    </Formik>
  );
};

export default CreatePostModal;
